/**
 * Minimal observable store over the pure state core. Framework-agnostic:
 * Preact components subscribe via a hook (M2); tests drive it in plain Node.
 */
import { initialState, type AppState } from './state.js';

export type Store = {
  getState(): AppState;
  /** Apply a pure transition to the current state and notify subscribers
   *  (no-op notification skip when the transition returns the same object). */
  update(transition: (s: AppState) => AppState): void;
  subscribe(listener: (s: AppState) => void): () => void;
};

export function createStore(initial: AppState = initialState()): Store {
  let state = initial;
  const listeners = new Set<(s: AppState) => void>();
  return {
    getState: () => state,
    update(transition) {
      const next = transition(state);
      if (next === state) return;
      state = next;
      for (const l of listeners) l(state);
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
